// src/components/DungeonMap.jsx
// Full copy-paste replacement — includes real Token Vault wiring

import React, { useState, useRef, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";

// ─────────────────────────────────────────────────────────────────────────────
//  DUNGEON — 13 rooms, 6 layers, forward-only movement
// ─────────────────────────────────────────────────────────────────────────────
const ROOMS = {
  entrance:     { name: "Entrance",      type: "start",    x: 65,  y: 180, desc: "Two dark passages split ahead. The dungeon breathes cold air." },
  west_hall:    { name: "West Hall",     type: "loot",     x: 185, y: 90,  desc: "Torchlight glints off abandoned chests. Relatively safe." },
  east_hall:    { name: "East Hall",     type: "trap",     x: 185, y: 270, desc: "Every flagstone is a pressure plate. −30 HP." },
  crypt:        { name: "Crypt",         type: "trap",     x: 315, y: 45,  desc: "Cursed remains wake underfoot. −30 HP." },
  barracks:     { name: "Barracks",      type: "loot",     x: 315, y: 170, desc: "Abandoned soldier quarters. Gold left behind." },
  swamp:        { name: "Poison Swamp",  type: "trap",     x: 315, y: 310, desc: "Toxic mist corrodes armor on contact. −30 HP." },
  library:      { name: "Dark Library",  type: "loot",     x: 440, y: 80,  desc: "Knowledge and gold are one here." },
  forge:        { name: "Forge",         type: "empty",    x: 440, y: 200, desc: "Cold furnaces. A rare neutral room. No danger." },
  deep_dungeon: { name: "Deep Dungeon",  type: "trap",     x: 440, y: 325, desc: "The lowest pits. Survivors are rare. −30 HP." },
  antechamber:  { name: "Antechamber",   type: "loot",     x: 565, y: 120, desc: "The boss's outer chamber. Spoils of the fallen." },
  cursed_hall:  { name: "Cursed Hall",   type: "trap",     x: 565, y: 265, desc: "Dark magic bleeds from the walls. −30 HP." },
  miniboss:     { name: "Warden Gate",   type: "miniboss", x: 685, y: 180, desc: "The Warden blocks the final door. Costs 40 HP to pass." },
  throne:       { name: "Throne Room",   type: "boss",     x: 785, y: 180, desc: "The Final Boss rises from the ancient throne." },
};

const CONNECTIONS = [
  ["entrance","west_hall"], ["entrance","east_hall"],
  ["west_hall","crypt"],    ["west_hall","barracks"],
  ["east_hall","barracks"], ["east_hall","swamp"],
  ["crypt","library"],      ["barracks","library"],
  ["barracks","forge"],     ["swamp","forge"],
  ["swamp","deep_dungeon"], ["library","antechamber"],
  ["forge","antechamber"],  ["forge","cursed_hall"],
  ["deep_dungeon","cursed_hall"],
  ["antechamber","miniboss"], ["cursed_hall","miniboss"],
  ["miniboss","throne"],
];

const ADJ = {};
Object.keys(ROOMS).forEach(r => { ADJ[r] = []; });
CONNECTIONS.forEach(([a, b]) => ADJ[a].push(b));

const REWARD  = { start: 0, loot: 20, empty: 5, trap: -30, miniboss: -20, boss: 150 };
const HP_COST = { trap: 30, miniboss: 40 };

// ─────────────────────────────────────────────────────────────────────────────
//  TOKEN VAULT POWERS
//  Each power requires the agent to have an OAuth token stored in Auth0
//  Token Vault. The user connects their GitHub account once; Auth0 stores
//  the token securely and the agent retrieves it without ever seeing credentials.
// ─────────────────────────────────────────────────────────────────────────────
const POWERS = {
  foresight:  { label: "Foresight",  icon: "👁️", scope: "github:read", desc: "Agent reads your GitHub repos to foresee danger — trap rooms are avoided during exploration." },
  resilience: { label: "Resilience", icon: "🛡️", scope: "github:read", desc: "Agent analyzes your commit history for persistence patterns — trap damage cut in half." },
  scavenger:  { label: "Scavenger",  icon: "💰", scope: "github:read", desc: "Agent mines your repo stars for resource patterns — double gold from loot rooms." },
};

// ─────────────────────────────────────────────────────────────────────────────
//  AUTH0 CONFIG — update these two values
// ─────────────────────────────────────────────────────────────────────────────
const AUTH0_DOMAIN   = "dev-7ohezt5oz21lioh6.us.auth0.com";
const AUTH0_AUDIENCE = "https://authdungeon-api";
const SPA_CLIENT_ID  = "zPsWjQrpoIrLw9SmGh0KHHAlf1QwK2Zh";

// ─────────────────────────────────────────────────────────────────────────────
//  Q-LEARNING — tuned for visible learning over 5-8 runs
// ─────────────────────────────────────────────────────────────────────────────
const LR = 0.35, GAMMA = 0.88, EPS0 = 0.90, EPS_DECAY = 0.82, EPS_MIN = 0.05, STEP_MS = 850;

const makeQ = () => {
  const q = {};
  Object.keys(ROOMS).forEach(r => { q[r] = {}; ADJ[r].forEach(n => { q[r][n] = 0; }); });
  return q;
};

export default function DungeonMap() {
  const { isAuthenticated, loginWithRedirect, logout, user, getAccessTokenSilently } = useAuth0();

  // All mutable game state in a ref — prevents stale closure bugs in timer loop
  const G = useRef({
    q: makeQ(), current: "entrance", path: ["entrance"],
    hp: 100, gold: 0, eps: EPS0, run: 1, bestPath: null, powers: [],
  });
  const timerRef   = useRef(null);
  const runningRef = useRef(false);
  const stepFnRef  = useRef(null);

  const [snap,     setSnap]     = useState({ current: "entrance", path: ["entrance"], hp: 100, gold: 0, eps: EPS0, run: 1, bestPath: null, q: makeQ() });
  const [log,      setLog]      = useState(["⚔️  The dungeon calls. AI memory is empty."]);
  const [running,  setRunning]  = useState(false);
  const [powers,   setPowers]   = useState([]);
  const [showVault,setShowVault]= useState(false);
  const [vaultLoading, setVaultLoading] = useState(null);

  const pushLog  = msg => setLog(l => [...l.slice(-9), msg]);
  const syncSnap = () => {
    const g = G.current;
    setSnap({ current: g.current, path: [...g.path], hp: g.hp, gold: g.gold, eps: g.eps, run: g.run, bestPath: g.bestPath, q: JSON.parse(JSON.stringify(g.q)) });
  };

  // ── Handle returning from GitHub OAuth connect flow ───────────────────────
  useEffect(() => {
  if (!isAuthenticated) return;
  
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  
  if (code) {
    window.history.replaceState({}, document.title, "/");
    
    const pendingPower = localStorage.getItem("pendingPower");
    console.log("Returned from GitHub. pendingPower:", pendingPower);
    
    if (pendingPower && POWERS[pendingPower]) {
      localStorage.removeItem("pendingPower");
      G.current.powers = [...new Set([...G.current.powers, pendingPower])];
      setPowers(p => [...new Set([...p, pendingPower])]);
      pushLog(`🔐 Token Vault: "${POWERS[pendingPower].label}" granted — GitHub token stored`);
      syncSnap();
    } else {
      // pendingPower was lost — grant whichever power triggered the redirect
      pushLog("🔐 GitHub connected — Token stored in Auth0 Token Vault");
    }
  }
}, [isAuthenticated]);

  // ── Token Vault: grant power ───────────────────────────────────────────────
  // Gets the user's Auth0 access token, sends it to Spring Boot backend,
  // backend exchanges it for GitHub token via Auth0 Token Vault API.
  const grantPower = useCallback(async (id) => {
  localStorage.setItem("pendingPower", id);
  console.log("Saved pendingPower:", id);
  setVaultLoading(id);
  try {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: AUTH0_AUDIENCE,
        scope: "openid profile email",
      }
    });

    const res = await fetch("http://localhost:8080/api/vault/token", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ connection: "github" })
    });

    const data = await res.json();

    if (data.status === "success") {
      G.current.powers = [...new Set([...G.current.powers, id])];
      setPowers(p => [...new Set([...p, id])]);
      pushLog(`🔐 Token Vault: "${POWERS[id].label}" granted via Auth0`);
      syncSnap();
    } else {
      // Any error including 500 — redirect to GitHub connect flow
      pushLog("🔗 Connecting GitHub via Auth0 Token Vault...");
      setTimeout(() => {
        localStorage.setItem("pendingPower", id);
        window.location.href =
          `https://${AUTH0_DOMAIN}/authorize` +
          `?response_type=code` +
          `&client_id=${SPA_CLIENT_ID}` +
          `&connection=github` +
          `&redirect_uri=${encodeURIComponent(window.location.origin)}` +
          `&scope=${encodeURIComponent("openid profile email")}` +
          `&audience=${encodeURIComponent(AUTH0_AUDIENCE)}` +
          `&prompt=consent`;
      }, 800);
    }
  } catch (err) {
    console.error("Token Vault error:", err);
    pushLog("🔗 Redirecting to GitHub via Auth0...");
    setTimeout(() => {
      window.location.href =
        `https://${AUTH0_DOMAIN}/authorize` +
        `?response_type=code` +
        `&client_id=${SPA_CLIENT_ID}` +
        `&connection=github` +
        `&redirect_uri=${encodeURIComponent(window.location.origin)}` +
        `&scope=${encodeURIComponent("openid profile email")}` +
        `&audience=${encodeURIComponent(AUTH0_AUDIENCE)}` +
        `&prompt=consent`;
    }, 800);
  }
  setVaultLoading(null);
}, [getAccessTokenSilently]);

  const revokePower = useCallback((id) => {
    G.current.powers = G.current.powers.filter(p => p !== id);
    setPowers(p => p.filter(x => x !== id));
    pushLog(`🚫 Token Vault: "${POWERS[id].label}" revoked — agent permission removed`);
    syncSnap();
  }, []);

  // ── AI Step — ref prevents stale closures ────────────────────────────────
  stepFnRef.current = function doStep() {
    if (!runningRef.current) return;
    const g = G.current, room = g.current, nbrs = ADJ[room];
    if (!nbrs.length) { endRun(); return; }

    const hasForesight  = g.powers.includes("foresight");
    const hasResilience = g.powers.includes("resilience");
    const hasScavenger  = g.powers.includes("scavenger");

    let next, logMsg;
    if (Math.random() < g.eps) {
      let pool = nbrs;
      if (hasForesight) {
        const safe = nbrs.filter(n => ROOMS[n].type !== "trap");
        if (safe.length) pool = safe;
      }
      next   = pool[Math.floor(Math.random() * pool.length)];
      logMsg = hasForesight
        ? `👁️ Foresight+explore → ${ROOMS[next].name}`
        : `🎲 Exploring → ${ROOMS[next].name}`;
    } else {
      next   = nbrs.reduce((b, n) => g.q[room][n] > g.q[room][b] ? n : b, nbrs[0]);
      logMsg = `🧠 Best learned path → ${ROOMS[next].name} (Q=${g.q[room][next].toFixed(1)})`;
    }

    const rtype = ROOMS[next].type, reward = REWARD[rtype] ?? 0, cost = HP_COST[rtype] ?? 0;
    const actualCost = hasResilience ? Math.floor(cost * 0.5) : cost;
    if (actualCost > 0) { g.hp = Math.max(0, g.hp - actualCost); logMsg += hasResilience ? `  🛡️ −${actualCost}hp (halved)` : `  ⚠️ −${actualCost}hp`; }
    if (rtype === "loot")     { const gained = hasScavenger ? 50 : 25; g.gold += gained; logMsg += `  💰 +${gained}g${hasScavenger ? " (×2)" : ""}`; }
    if (rtype === "miniboss") logMsg += "  ⚔️ WARDEN!";
    if (rtype === "boss")     logMsg += "  👑 BOSS!";
    if (g.hp <= 0)            logMsg += "  💀 DIED";

    const fvs = ADJ[next].map(n => g.q[next][n]);
    g.q[room][next] += LR * (reward + GAMMA * (fvs.length ? Math.max(...fvs) : 0) - g.q[room][next]);
    g.path = [...g.path, next]; g.current = next;
    pushLog(logMsg); syncSnap();
    (next === "throne" || g.hp <= 0) ? endRun() : (timerRef.current = setTimeout(() => stepFnRef.current(), STEP_MS));
  };

  function endRun() {
    const g = G.current, won = g.current === "throne";
    pushLog(won
      ? `🏆 Run ${g.run} — boss defeated! (${g.path.length - 1} rooms, ${g.gold}g)`
      : `💀 Run ${g.run} — died  (${g.hp}hp left)`
    );
    if (won && (!g.bestPath || g.path.length < g.bestPath.length)) g.bestPath = [...g.path];
    g.eps = Math.max(EPS_MIN, g.eps * EPS_DECAY);
    timerRef.current = setTimeout(() => {
      if (!runningRef.current) return;
      g.run++; g.current = "entrance"; g.path = ["entrance"]; g.hp = 100; g.gold = 0;
      pushLog(`──────── Run ${g.run} ────────`); syncSnap();
      timerRef.current = setTimeout(() => stepFnRef.current(), STEP_MS);
    }, 1400);
  }

  const startAI  = useCallback(() => { if (runningRef.current) return; runningRef.current = true; setRunning(true); pushLog(`──────── Run ${G.current.run} ────────`); timerRef.current = setTimeout(() => stepFnRef.current(), STEP_MS); }, []);
  const stopAI   = useCallback(() => { clearTimeout(timerRef.current); runningRef.current = false; setRunning(false); }, []);
  const resetAll = useCallback(() => { clearTimeout(timerRef.current); runningRef.current = false; G.current = { q: makeQ(), current: "entrance", path: ["entrance"], hp: 100, gold: 0, eps: EPS0, run: 1, bestPath: null, powers: G.current.powers }; setRunning(false); setLog(["🔄 AI memory wiped. Token Vault powers retained."]); syncSnap(); }, []);
  const manualMove = useCallback(room => {
    if (runningRef.current) return;
    if (!ADJ[G.current.current].includes(room)) return;
    const g = G.current, rtype = ROOMS[room].type, c = HP_COST[rtype] ?? 0;
    if (c) { g.hp = Math.max(0, g.hp - c); pushLog(`⚠️ −${c}hp`); }
    if (rtype === "loot") { g.gold += 25; pushLog("💰 +25g"); }
    if (rtype === "boss") pushLog("👑 Final Boss!");
    g.path = [...g.path, room]; g.current = room; syncSnap();
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // ── Colors ────────────────────────────────────────────────────────────────
  const roomFill = id => {
    if (id === snap.current)       return "#8b1a1a";
    if (id === "throne")           return "#3b1254";
    if (id === "miniboss")         return "#3d1c00";
    if (id === "entrance")         return "#0f3d1a";
    if (snap.path.includes(id))    return "#4d2608";
    const t = ROOMS[id].type;
    if (t === "trap")  return "#151825";
    if (t === "loot")  return "#0a1928";
    if (t === "empty") return "#181818";
    return "#111";
  };
  const pathLit = (a, b) => { const ai = snap.path.indexOf(a), bi = snap.path.indexOf(b); return ai !== -1 && bi !== -1 && bi === ai + 1; };
  const qNow = snap.q[snap.current] || {};

  // ── Login gate ────────────────────────────────────────────────────────────
  if (!isAuthenticated) return (
    <div style={{ minHeight: "100vh", background: "#080808", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#fff", gap: "1.4rem", fontFamily: "Georgia,serif" }}>
      <div style={{ fontSize: "5rem", filter: "drop-shadow(0 0 28px #c0392baa)" }}>⚔️</div>
      <h1 style={{ color: "#e74c3c", fontSize: "3rem", margin: 0, letterSpacing: "0.04em" }}>AuthDungeon</h1>
      <p style={{ color: "#888", maxWidth: "360px", textAlign: "center", lineHeight: "1.8", fontSize: "1rem" }}>
        An AI agent that learns to conquer dungeons.<br />
        <span style={{ color: "#555" }}>Agent permissions secured by Auth0 Token Vault.</span>
      </p>
      <button onClick={() => loginWithRedirect()} style={{ background: "#c0392b", color: "#fff", border: "none", padding: "14px 44px", fontSize: "1.1rem", borderRadius: "7px", cursor: "pointer", letterSpacing: "0.05em", fontFamily: "Georgia,serif" }}>
        🔐 Login to Enter
      </button>
    </div>
  );

  // ── Main UI ───────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#080808", color: "#e0e0e0", fontFamily: "Georgia,serif", padding: "0.8rem 1.1rem" }}>

      {/* Nav */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #222", paddingBottom: "0.55rem", marginBottom: "0.75rem" }}>
        <h1 style={{ margin: 0, color: "#e74c3c", fontSize: "1.7rem", letterSpacing: "0.04em" }}>⚔️ AuthDungeon</h1>
        <div style={{ display: "flex", gap: "0.7rem", alignItems: "center" }}>
          <button onClick={() => setShowVault(v => !v)}
            style={{ background: showVault ? "#1a0808" : "none", color: powers.length ? "#e67e22" : "#555", border: `1px solid ${powers.length ? "#5a2800" : "#282828"}`, padding: "5px 13px", borderRadius: "5px", cursor: "pointer", fontSize: "0.85rem", fontFamily: "Georgia,serif" }}>
            🔐 Token Vault {powers.length ? `(${powers.length} active)` : ""}
          </button>
          <span style={{ color: "#444", fontSize: "0.85rem" }}>{user?.name}</span>
          <button onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })} style={{ background: "none", color: "#444", border: "1px solid #222", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "0.78rem" }}>logout</button>
        </div>
      </div>

      {/* Token Vault panel */}
      {showVault && (
        <div style={{ background: "#0c0404", border: "1px solid #3a1200", borderRadius: "8px", padding: "1rem 1.1rem", marginBottom: "0.75rem" }}>
          <div style={{ color: "#e67e22", fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "0.4rem", fontWeight: "bold" }}>
            🔐 Auth0 Token Vault — Agent Permissions
          </div>
          <p style={{ color: "#777", fontSize: "0.85rem", margin: "0 0 0.85rem", lineHeight: "1.6" }}>
            Grant OAuth-scoped powers to the AI agent. Auth0 stores your GitHub token securely — the agent uses it without ever seeing your credentials.
          </p>
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            {Object.entries(POWERS).map(([id, def]) => {
              const on = powers.includes(id);
              return (
                <div key={id} style={{ background: on ? "#1a0e00" : "#111", border: `1px solid ${on ? "#5a3200" : "#222"}`, borderRadius: "7px", padding: "0.8rem 1rem", flex: 1, minWidth: "160px" }}>
                  <div style={{ fontSize: "1.6rem", marginBottom: "0.3rem" }}>{def.icon}</div>
                  <div style={{ color: on ? "#e67e22" : "#888", fontWeight: "bold", fontSize: "0.95rem", marginBottom: "0.25rem" }}>{def.label}</div>
                  <div style={{ color: "#555", fontSize: "0.8rem", marginBottom: "0.3rem", lineHeight: "1.5" }}>{def.desc}</div>
                  <div style={{ color: "#333", fontSize: "0.72rem", marginBottom: "0.65rem", fontStyle: "italic" }}>scope: {def.scope}</div>
                  {on
                    ? <button onClick={() => revokePower(id)} style={{ background: "none", color: "#663300", border: "1px solid #3d1e00", padding: "5px 12px", borderRadius: "5px", cursor: "pointer", fontSize: "0.8rem", width: "100%", fontFamily: "Georgia,serif" }}>Revoke Access</button>
                    : <button onClick={() => grantPower(id)} disabled={vaultLoading === id}
                        style={{ background: vaultLoading === id ? "#111" : "#3d1800", color: vaultLoading === id ? "#333" : "#e67e22", border: "none", padding: "5px 12px", borderRadius: "5px", cursor: vaultLoading === id ? "wait" : "pointer", fontSize: "0.8rem", width: "100%", fontFamily: "Georgia,serif" }}>
                        {vaultLoading === id ? "Authorizing via Auth0…" : "Grant via Auth0"}
                      </button>
                  }
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 275px", gap: "0.75rem" }}>

        {/* ═══ LEFT ═══ */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>

          {/* Stats bar */}
          <div style={{ display: "flex", gap: "1.5rem", background: "#0e0e0e", border: "1px solid #1e1e1e", borderRadius: "6px", padding: "0.55rem 1rem", fontSize: "0.92rem", flexWrap: "wrap", alignItems: "center" }}>
            <span>❤️ <span style={{ color: snap.hp < 40 ? "#e74c3c" : snap.hp < 70 ? "#e67e22" : "#2ecc71", fontWeight: "bold", fontSize: "1rem" }}>{snap.hp} HP</span></span>
            <span style={{ color: "#ccc" }}>💰 <b>{snap.gold}</b>g</span>
            <span style={{ color: "#ccc" }}>🏃 Run <b style={{ color: "#fff" }}>{snap.run}</b></span>
            <span>🔭 Explore <span style={{ color: "#e67e22", fontWeight: "bold" }}>{(snap.eps * 100).toFixed(0)}%</span></span>
            {powers.length > 0 && <span style={{ color: "#8b5e00" }}>{powers.map(p => POWERS[p].icon).join(" ")} active</span>}
            {snap.bestPath && <span style={{ color: "#8b7020", marginLeft: "auto" }}>✨ Best run: <b>{snap.bestPath.length - 1}</b> rooms</span>}
          </div>

          {/* SVG Map */}
          <div style={{ background: "#0b0b0b", border: "1px solid #1e1e1e", borderRadius: "9px", padding: "8px" }}>
            <svg width="100%" viewBox="0 0 830 365" style={{ display: "block" }}>
              {CONNECTIONS.map(([a, b], i) => {
                const ra = ROOMS[a], rb = ROOMS[b], lit = pathLit(a, b);
                return <line key={i} x1={ra.x} y1={ra.y} x2={rb.x} y2={rb.y} stroke={lit ? "#8b3a00" : "#242424"} strokeWidth={lit ? 3.5 : 1.8} strokeDasharray={lit ? "" : "6,5"} />;
              })}
              {Object.entries(ROOMS).map(([id, r]) => {
                const isCur = id === snap.current, isAvail = !running && ADJ[snap.current].includes(id), isVisit = snap.path.includes(id);
                return (
                  <g key={id} onClick={() => manualMove(id)} style={{ cursor: isAvail ? "pointer" : "default" }}>
                    {isCur && <circle cx={r.x} cy={r.y} r={36} fill="none" stroke="#e74c3c" strokeWidth={1.2} opacity={0.18}><animate attributeName="r" values="32;39;32" dur="1.7s" repeatCount="indefinite" /></circle>}
                    {isAvail && <rect x={r.x - 48} y={r.y - 27} width={96} height={54} rx={9} fill="none" stroke="#8b5e00" strokeWidth={2} strokeDasharray="5,3"><animate attributeName="opacity" values="0.7;0.2;0.7" dur="1.0s" repeatCount="indefinite" /></rect>}
                    <rect x={r.x - 45} y={r.y - 25} width={90} height={50} rx={8}
                      fill={roomFill(id)}
                      stroke={isCur ? "#e74c3c" : isAvail ? "#8b5e00" : isVisit ? "#3a2010" : ROOMS[id].type === "miniboss" ? "#5a2e00" : ROOMS[id].type === "boss" ? "#3b1254" : "#1e1e1e"}
                      strokeWidth={isCur ? 2.8 : isAvail ? 2 : 1.2}
                    />
                    <text x={r.x} y={r.y - 5} textAnchor="middle" fill={isCur ? "#ffffff" : isVisit ? "#c0844a" : "#888888"} fontSize="11.5" fontWeight="bold" fontFamily="Georgia,serif">{r.name}</text>
                    <text x={r.x} y={r.y + 12} textAnchor="middle" fill={isCur ? "#ddd" : "#444"} fontSize="10" fontFamily="Georgia,serif">
                      {ROOMS[id].type === "trap" ? "☠️ danger" : ROOMS[id].type === "loot" ? "💰 treasure" : ROOMS[id].type === "empty" ? "⬜ safe" : ROOMS[id].type === "miniboss" ? "⚔️ warden" : ROOMS[id].type === "boss" ? "👑 boss" : "🚪 start"}
                    </text>
                    {isCur && <circle cx={r.x} cy={r.y + 34} r={5} fill="#e74c3c"><animate attributeName="r" values="4;6;4" dur="0.9s" repeatCount="indefinite" /></circle>}
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Controls */}
          <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
            <button onClick={startAI} disabled={running} style={{ background: running ? "#151515" : "#8b1a1a", color: running ? "#444" : "#ffffff", border: "none", padding: "10px 26px", borderRadius: "6px", cursor: running ? "not-allowed" : "pointer", fontSize: "0.95rem", fontFamily: "Georgia,serif" }}>
              {running ? "🤖 AI Running…" : "▶  Run AI"}
            </button>
            <button onClick={stopAI} disabled={!running} style={{ background: "#111", color: running ? "#bbb" : "#333", border: "1px solid #222", padding: "10px 18px", borderRadius: "6px", cursor: running ? "pointer" : "not-allowed", fontFamily: "Georgia,serif", fontSize: "0.92rem" }}>
              ⏸  Pause
            </button>
            <button onClick={resetAll} style={{ background: "none", color: "#555", border: "1px solid #1e1e1e", padding: "10px 18px", borderRadius: "6px", cursor: "pointer", fontFamily: "Georgia,serif", fontSize: "0.92rem" }}>
              🔄  Reset AI
            </button>
          </div>
          <p style={{ color: "#333", fontSize: "0.8rem", margin: 0, lineHeight: "1.5" }}>
            {running
              ? `AI explore rate drops each run as it learns. Watch the Q-values update.${powers.includes("foresight") ? " Foresight active: trap rooms filtered from exploration." : ""}`
              : "Yellow-outlined rooms are reachable — click to move manually. Press Run AI to watch the agent learn."}
          </p>
        </div>

        {/* ═══ RIGHT ═══ */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>

          {/* Current room */}
          <div style={{ background: "#0e0e0e", border: "1px solid #1e1e1e", borderRadius: "7px", padding: "0.85rem" }}>
            <div style={{ color: "#7b1c24", fontSize: "0.7rem", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: "0.4rem", fontWeight: "bold" }}>Current Room</div>
            <div style={{ fontWeight: "bold", fontSize: "1.05rem", color: "#ffffff", marginBottom: "0.3rem" }}>{ROOMS[snap.current].name}</div>
            <div style={{ color: "#666", fontSize: "0.85rem", lineHeight: "1.6", fontStyle: "italic" }}>{ROOMS[snap.current].desc}</div>
          </div>

          {/* AI Reasoning log */}
          <div style={{ background: "#0e0e0e", border: "1px solid #1e1e1e", borderRadius: "7px", padding: "0.85rem", flex: 1 }}>
            <div style={{ color: "#7b1c24", fontSize: "0.7rem", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: "0.55rem", fontWeight: "bold" }}>🧠 AI Reasoning</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              {[...log].reverse().map((l, i) => (
                <div key={i} style={{ fontSize: "0.82rem", color: i === 0 ? "#e0e0e0" : i === 1 ? "#888" : i < 4 ? "#555" : "#303030", lineHeight: "1.6", borderBottom: "1px solid #161616", paddingBottom: "0.22rem" }}>
                  {l}
                </div>
              ))}
            </div>
          </div>

          {/* Q-values */}
          <div style={{ background: "#0e0e0e", border: "1px solid #1e1e1e", borderRadius: "7px", padding: "0.85rem" }}>
            <div style={{ color: "#7b1c24", fontSize: "0.7rem", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: "0.5rem", fontWeight: "bold" }}>📊 Learned Values</div>
            {Object.keys(qNow).length === 0
              ? <div style={{ color: "#333", fontSize: "0.82rem" }}>No exits from here.</div>
              : Object.entries(qNow).sort(([, a], [, b]) => b - a).map(([room, val]) => (
                <div key={room} style={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem", padding: "4px 0", borderBottom: "1px solid #141414", alignItems: "center" }}>
                  <span style={{ color: "#777" }}>{ROOMS[room]?.name}</span>
                  <span style={{ color: val > 10 ? "#2ecc71" : val < -10 ? "#e74c3c" : "#555", fontWeight: "bold", fontSize: "0.9rem" }}>{val.toFixed(1)}</span>
                </div>
              ))
            }
            <div style={{ color: "#2e2e2e", fontSize: "0.75rem", marginTop: "0.5rem" }}>Green = AI prefers · Red = AI avoids</div>
          </div>

        </div>
      </div>
    </div>
  );
}